import React from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStats, SortField, SortDirection } from '../../types';
import { StatsTableProps } from '../../types/components';

/**
 * A reusable component for displaying button statistics in a table
 */
function StatsTable({ stats, sortField = 'totalClicks', sortDirection = 'desc', onSort, formatLastClick }: StatsTableProps) {
  const { t } = useTranslation();

  if (stats.length === 0) {
    return null;
  }

  // Find the maximum total clicks value for the progress bar calculation
  const maxTotalClicks = Math.max(...stats.map(stat => stat.totalClicks));

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body p-3">
        <h5 className="card-title mb-3 fs-6">{t('buttons.statistics')}</h5>
        <div className="table-responsive">
          <table className="table table-hover align-middle table-sm">
            <thead>
              <tr>
                <th className="cursor-pointer" onClick={() => onSort('name')}>
                  {t('history.button')}
                  {sortField === 'name' && (
                    <i className={`bi bi-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                  )}
                </th>
                <th className="cursor-pointer d-none d-md-table-cell" onClick={() => onSort('lastClick')}>
                  {t('buttons.lastClick')}
                  {sortField === 'lastClick' && (
                    <i className={`bi bi-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                  )}
                </th>
                <th className="d-table-cell d-md-table-cell">
                  {t('buttons.progress', 'Progress')}
                </th>
                <th className="cursor-pointer text-end" onClick={() => onSort('totalClicks')}>
                  {t('buttons.total')}
                  {sortField === 'totalClicks' && (
                    <i className={`bi bi-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {stats.map(stat => (
                <tr key={stat._id}>
                  <td>
                    <div className="d-flex align-items-center gap-2 button-name" style={{ '--button-color': stat.color } as React.CSSProperties}>
                      <i className={`${stat.icon} icon-medium icon`}></i>
                      <span className="fw-medium small text-truncate">{stat.name}</span>
                    </div>
                  </td>
                  <td className="d-none d-md-table-cell">
                    {formatLastClick(stat.lastClick)}
                  </td>
                  <td className="d-table-cell d-md-table-cell">
                    <div className="progress" role="progressbar" 
                         aria-valuenow={stat.totalClicks} 
                         aria-valuemin={0} 
                         aria-valuemax={maxTotalClicks}
                         style={{height: "15px"}}>
                      <div className="progress-bar" 
                           style={{
                             width: `${maxTotalClicks > 0 ? (stat.totalClicks / maxTotalClicks * 100) : 0}%`,
                             backgroundColor: stat.color
                           }}>
                        <span className="visually-hidden">{`${Math.round(maxTotalClicks > 0 ? (stat.totalClicks / maxTotalClicks * 100) : 0)}%`}</span>
                      </div>
                    </div>
                  </td>
                  <td className="text-end fw-medium">{stat.totalClicks}</td>
                </tr>
              ))}
              {stats.length > 0 && (
                <tr className="table-primary fw-bold">
                  <td>{t('buttons.total')}</td>
                  <td className="d-none d-md-table-cell">
                    {formatLastClick(new Date(Math.max(...stats.map(stat => stat.lastClick ? new Date(stat.lastClick).getTime() : 0))).toISOString())}
                  </td>
                  <td className="d-table-cell d-md-table-cell">
                    {/* No progress bar in total row */}
                  </td>
                  <td className="text-end">{stats.reduce((sum, stat) => sum + stat.totalClicks, 0)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default StatsTable; 